import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { TrendingUp, Target, ShieldAlert, FileSearch, LineChart as LineChartIcon, AlertTriangle, ArrowRight, CheckCircle2, BarChart2, Download, Loader2 } from 'lucide-react';
import ReactECharts from 'echarts-for-react';

export default function CorpusInsights() {
  
    const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef(null);

  const handleExport = async () => {
    if (!reportRef.current) return;
    try {
      setIsExporting(true);
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, // higher resolution
        useCORS: true,
        backgroundColor: document.documentElement.classList.contains('dark') ? '#0f172a' : '#f8fafc',
      });
      canvas.toBlob((blob) => {
        const brandName = localStorage.getItem('globalBrandName') || 'Brand';
        const dateStr = new Date().toISOString().split('T')[0];
        saveAs(blob, `NexGEO_水质诊断报告_${brandName}_${dateStr}.png`);
        setIsExporting(false);
      }, 'image/png');
    } catch (err) {
      console.error('Export failed:', err);
      setIsExporting(false);
    }
  };

  const fetchInsights = async () => {
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') {
      setInsightsData(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`/api/corpus/insights-data?projectId=${projectId}`);
      setInsightsData(res.data.data);
    } catch(err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
    window.addEventListener('globalFilterChange', fetchInsights);
    return () => window.removeEventListener('globalFilterChange', fetchInsights);
  }, []);

  // Custom Color Palette
  const colors = {
    blue: '#3b82f6',
    red: '#ef4444',
    green: '#10b981',
    orange: '#f97316',
    purple: '#8b5cf6'
  };

  const isDark = document.documentElement.classList.contains('dark');
  const textColor = isDark ? '#cbd5e1' : '#1e293b';
  const gridColor = isDark ? '#334155' : '#e2e8f0';

  // 1. Line Chart Option (Water Quality Evolution - Red vs Blue Line)
  const lineOption = {
    tooltip: { 
      trigger: 'axis',
      formatter: function (params) {
        let res = `<div class="font-bold mb-1">${params[0].name}</div>`;
        params.forEach(p => {
          res += `<div class="flex items-center gap-2"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${p.color};"></span>${p.seriesName}: <span class="font-bold">${p.value}%</span></div>`;
        });
        return res;
      }
    },
    legend: {
      data: ['正面语料渗透率 (蓝线)', '敏感预警浓度 (红线)'],
      bottom: 0,
      textStyle: { color: textColor, fontWeight: 'bold' }
    },
    grid: { left: '3%', right: '6%', bottom: '15%', top: '5%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: insightsData?.trend.map(t => t.month) || [],
      axisLabel: { color: textColor, fontWeight: 'bold' },
      axisLine: { lineStyle: { color: gridColor } }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: textColor, formatter: '{value} %' },
      splitLine: { lineStyle: { type: 'dashed', color: gridColor } }
    },
    series: [
      {
        name: '正面语料渗透率 (蓝线)',
        type: 'line',
        smooth: true,
        symbolSize: 8,
        itemStyle: { color: colors.blue },
        lineStyle: { width: 4, color: colors.blue, shadowColor: 'rgba(59, 130, 246, 0.4)', shadowBlur: 10, shadowOffsetY: 5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(59, 130, 246, 0.3)' }, { offset: 1, color: 'rgba(59, 130, 246, 0.01)' }]
          }
        },
        data: insightsData?.trend.map(t => t.blueRate) || []
      },
      {
        name: '敏感预警浓度 (红线)',
        type: 'line',
        smooth: true,
        symbolSize: 8,
        itemStyle: { color: colors.red },
        lineStyle: { width: 4, color: colors.red, shadowColor: 'rgba(239, 68, 68, 0.4)', shadowBlur: 10, shadowOffsetY: 5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(239, 68, 68, 0.3)' }, { offset: 1, color: 'rgba(239, 68, 68, 0.01)' }]
          }
        },
        data: insightsData?.trend.map(t => t.redRate) || []
      }
    ]
  };

  // 2. Bar Chart Option (Sensitive Words Hit Breakdown)
  const barOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '8%', bottom: '15%', top: '5%', containLabel: true },
    xAxis: { 
      type: 'value', 
      name: '本月命中拦截次数',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: textColor },
      axisLabel: { color: textColor },
      splitLine: { lineStyle: { type: 'dashed', color: gridColor } }
    },
    yAxis: { 
      type: 'category', 
      data: (insightsData?.sensitiveBreakdown.map(b => b.name) || []).reverse(),
      axisLabel: { color: textColor, fontWeight: 'bold' },
      axisLine: { lineStyle: { color: gridColor } }
    },
    series: [
      {
        name: '命中次数',
        type: 'bar',
        data: (insightsData?.sensitiveBreakdown.map(b => b.value) || []).reverse(),
        itemStyle: {
          color: function(params) {
            // Gradient from red to orange based on value
            return params.value > 15 ? colors.red : colors.orange;
          },
          borderRadius: 4
        },
        label: {
          show: true,
          position: 'right',
          color: textColor,
          fontWeight: 'bold',
          formatter: '{c} 次'
        }
      }
    ]
  };

  if (loading) return <div className="p-6 h-full flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  if (!insightsData) return <div className="p-6 h-full flex flex-col items-center justify-center text-gray-500"><TrendingUp className="w-12 h-12 mb-4 text-gray-300"/><h3>请先选择具体的品牌项目以查看诊断大盘</h3></div>;

  return (
    <div className="p-6 h-full overflow-y-auto bg-slate-50 dark:bg-slate-900" ref={reportRef}>
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <LineChartIcon className="w-6 h-6 text-indigo-500" />
            生态水质演进与分析 (Insights)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            基于历史快照对比与大模型语义判定，全局追踪品牌在 AI 引擎中的健康度与阵地攻防态势。
          </p>
        </div>
        <button 
          onClick={handleExport}
          disabled={isExporting}
          className="mt-4 md:mt-0 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 font-medium transition-colors shadow-sm"
          data-html2canvas-ignore="true"
        >
          {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {isExporting ? '正在导出图片...' : '导出图片'}
        </button>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-center relative overflow-hidden">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
            <Target className="w-6 h-6 text-blue-500 opacity-50" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">全局生态清澈度</p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white">{insightsData ? insightsData.kpis.score : 0}<span className="text-xl">分</span></h3>
            <span className={`text-sm font-bold flex items-center mb-1 ${insightsData?.kpis.scoreDiff >= 0 ? 'text-green-500' : 'text-red-500'}`}>{insightsData?.kpis.scoreDiff >= 0 ? '↑' : '↓'} {Math.abs(insightsData?.kpis.scoreDiff || 0)}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 rounded-xl p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm flex flex-col justify-center">
          <p className="text-sm text-blue-800 dark:text-blue-300 font-bold mb-1 flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500"></div> 正面卖点渗透率 (蓝线)</p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-bold text-blue-600 dark:text-blue-400">{insightsData ? insightsData.kpis.blueRate : 0}<span className="text-xl">%</span></h3>
            <span className={`text-sm font-bold mb-1 ${insightsData?.kpis.blueDiff >= 0 ? 'text-green-500' : 'text-red-500'}`}>{insightsData?.kpis.blueDiff >= 0 ? '↑' : '↓'} {Math.abs(insightsData?.kpis.blueDiff || 0)}%</span>
          </div>
          <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-2">大模型语义模糊命中率</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-slate-800 rounded-xl p-5 border border-red-100 dark:border-red-800/50 shadow-sm flex flex-col justify-center">
          <p className="text-sm text-red-800 dark:text-red-300 font-bold mb-1 flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500"></div> 敏感负面浓度 (红线)</p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-bold text-red-600 dark:text-red-400">{insightsData ? insightsData.kpis.redRate : 0}<span className="text-xl">%</span></h3>
            <span className={`text-sm font-bold mb-1 ${insightsData?.kpis.redDiff <= 0 ? 'text-green-500' : 'text-red-500'}`}>{insightsData?.kpis.redDiff <= 0 ? '↓' : '↑'} {Math.abs(insightsData?.kpis.redDiff || 0)}%</span>
          </div>
          <p className="text-xs text-red-600/70 dark:text-red-400/70 mt-2">剔除“竞品躺枪”后的净浓度</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">累计监控快照样本</p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white">{insightsData ? insightsData.totalSamples : 0}</h3>
            <span className="text-gray-400 text-sm mb-1">次问答抓取</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Line Chart - The Core Water Quality Chart */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 lg:col-span-2 flex flex-col">
          <div className="flex items-center justify-between mb-4 shrink-0">
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 text-lg">
                <LineChartIcon className="w-5 h-5 text-indigo-500" /> 品牌溯源水质演进大盘 (红蓝交锋)
              </h3>
              <p className="text-xs text-gray-500 mt-1">展示近 6 个月内，AI 引擎引用中【我方采纳的正面核心卖点】与【敏感高危词汇】的此消彼长态势。</p>
            </div>
          </div>
          <div className="flex-1 min-h-[350px]">
            <ReactECharts option={lineOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        {/* Bar Chart - Sensitive Breakdown */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4 shrink-0">
            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-500" /> 敏感/负面诱因拆解
            </h3>
          </div>
          <p className="text-xs text-gray-500 mb-2">排除了由于提到竞品引发的误杀，仅展示本月真实攻击品牌本体的词频分布。</p>
          <div className="flex-1 min-h-[300px]">
            <ReactECharts option={barOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>
      </div>

      
      {/* AI Actionable Recommendations */}
      <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-4 flex items-center gap-2">
        <FileSearch className="w-5 h-5 text-indigo-500" /> 智能诊断与行动策略
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        
        {insightsData?.recommendations?.map((rec, idx) => {
          let styleClass = "";
          let IconComponent = Target;
          let btnColor = "";
          
          if (rec.type === 'blue') {
            styleClass = "bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 border border-blue-200 dark:border-blue-800/50";
            IconComponent = Target;
            btnColor = "text-blue-600";
          } else if (rec.type === 'red') {
            styleClass = "bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-slate-800 border border-red-200 dark:border-red-800/50";
            IconComponent = AlertTriangle;
            btnColor = "text-red-600";
          } else if (rec.type === 'green') {
            styleClass = "bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-slate-800 border border-green-200 dark:border-green-800/50";
            IconComponent = CheckCircle2;
            btnColor = "text-green-600";
          } else if (rec.type === 'gray') {
            styleClass = "bg-gradient-to-br from-gray-50 to-white dark:from-slate-800/50 dark:to-slate-800 border border-gray-200 dark:border-slate-700";
            IconComponent = FileSearch;
            btnColor = "text-gray-500 dark:text-gray-400";
          }

          return (
            <div key={idx} className={`${styleClass} rounded-xl p-6 relative overflow-hidden group hover:shadow-md transition-shadow`}>
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <IconComponent className={`w-24 h-24 ${btnColor}`} />
              </div>
              <div className="relative z-10">
                <span className={`${rec.type === 'blue' ? 'bg-blue-600' : rec.type === 'red' ? 'bg-red-600' : rec.type === 'gray' ? 'bg-gray-500' : 'bg-green-600'} text-white text-xs font-bold px-2 py-1 rounded mb-3 inline-block shadow-sm`}>
                  {rec.tag}
                </span>
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{rec.title}</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">
                  {rec.description}
                </p>
                {rec.action && (
                  <button onClick={() => window.location.href=rec.action.link} className={`${btnColor} font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all`}>
                    {rec.action.label} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
}
